import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { site } from "@/config/site";
import { PublicPrimaryCta } from "@/features/public/components/PublicPrimaryCta";
import { PublicSessionProvider, usePublicSession } from "@/features/public/hooks/PublicSessionContext";
import { BrandLogo } from "@/features/layout/components/BrandLogo";
import { cn } from "@/utils/cn";

function navClass({ isActive }: { isActive: boolean }) {
  return cn(isActive && "active");
}

function PublicSiteFooter() {
  const session = usePublicSession();
  const year = new Date().getFullYear();

  return (
    <footer className="pub-site-footer">
      <div className="pub-site-footer-grid">
        <div>
          <Link to={ROUTES.home} className="no-underline">
            <BrandLogo iconClassName="h-7 w-7" className="text-[0.9rem]" />
          </Link>
          <p className="pub-site-footer-tagline">
            PDF export for Stripe merchants. By{" "}
            <span className="whitespace-nowrap">{site.brandName}</span>.
          </p>
        </div>
        <div>
          <h3>Product</h3>
          <ul>
            <li>
              <Link to={ROUTES.pricing}>Pricing</Link>
            </li>
            <li>
              <Link to={ROUTES.docs}>Documentation</Link>
            </li>
            <li>
              <Link to={ROUTES.help}>FAQ</Link>
            </li>
            {session === "authenticated" ? (
              <li>
                <Link to={ROUTES.dashboard}>Dashboard</Link>
              </li>
            ) : null}
          </ul>
        </div>
        <div>
          <h3>Legal</h3>
          <ul>
            <li>
              <Link to={ROUTES.privacy}>Privacy</Link>
            </li>
            <li>
              <Link to={ROUTES.terms}>Terms</Link>
            </li>
          </ul>
        </div>
        <div>
          <h3>Support</h3>
          <ul>
            <li>
              <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>
            </li>
            <li>
              <a href={site.stripeDashboardUrl} target="_blank" rel="noreferrer">
                Stripe Dashboard ↗
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="pub-site-footer-bottom">
        <span>
          © {year} <span className="whitespace-nowrap">{site.brandName}</span>
        </span>
        <span>Built for Stripe</span>
      </div>
    </footer>
  );
}

function PublicSiteLayoutInner() {
  const location = useLocation();
  const docsActive = location.pathname === ROUTES.docs;

  return (
    <div className="pub-site">
      <header className="pub-site-header">
        <div className="pub-site-header-inner">
          <Link to={ROUTES.home} className="no-underline">
            <BrandLogo iconClassName="h-7 w-7" className="text-[0.9rem]" />
          </Link>
          <nav className="pub-site-nav" aria-label="Public site">
            <NavLink to={ROUTES.home} end className={navClass}>
              Home
            </NavLink>
            <NavLink to={ROUTES.pricing} className={navClass}>
              Pricing
            </NavLink>
            <NavLink to={ROUTES.docs} className={() => cn(docsActive && "active")}>
              Docs
            </NavLink>
            <NavLink to={ROUTES.help} className={navClass}>
              Help
            </NavLink>
            <PublicPrimaryCta variant="nav" />
          </nav>
        </div>
      </header>

      <main className="pub-site-main">
        <Outlet />
      </main>

      <PublicSiteFooter />
    </div>
  );
}

export function PublicSiteLayout() {
  return (
    <PublicSessionProvider>
      <PublicSiteLayoutInner />
    </PublicSessionProvider>
  );
}
